import React, { useState, useEffect } from 'react';
import { useLocation, useNavigate, useParams } from 'react-router-dom';
import { ShieldCheck, Plus, Minus, MapPin, CheckCircle2, Users } from 'lucide-react';
import { format } from 'date-fns';
import LoginModal from '../components/LoginModal';
import DefaultSEO from '../components/DefaultSEO';
import { openRazorpayModal } from '../utils/razorpay';

const BookingCheckoutPage = () => {
    const location = useLocation();
    const navigate = useNavigate();
    const { id } = useParams();
    const [loading, setLoading] = useState(false);
    const [error, setError] = useState('');

    const [showLoginModal, setShowLoginModal] = useState(false);

    useEffect(() => {
        const token = localStorage.getItem('auth_token');
        if (!token) {
            setShowLoginModal(true);
        }
    }, [navigate, location]);

    // Get booking data from state, fallback to empty/mock to avoid crashes
    const bookingData = location.state || {
        hotel: { name: "Taj Mahal Palace, Mumbai", location: "Mumbai, India", image: "https://images.unsplash.com/photo-1566073771259-6a8506099945?ixlib=rb-4.0.3&auto=format&fit=crop&w=400&q=80" },
        room: { type: "Luxury Grande Room" },
        dates: { startDate: new Date(), endDate: new Date(new Date().setDate(new Date().getDate() + 2)) },
        guests: 1,
        pricing: { totalNights: 2, totalPrice: 25000, taxes: 4500, finalAmount: 29500 }
    };

    let { hotel, room, dates, pricing, guests } = bookingData;

    // Ensure all pricing values are strictly Numbers to prevent string concatenation bugs
    pricing = {
        ...pricing,
        totalNights: Number(pricing.totalNights) || 1,
        totalPrice: Number(pricing.totalPrice) || 0,
        taxes: Number(pricing.taxes) || 0,
        finalAmount: Number(pricing.finalAmount) || 0
    };

    const [guestCount, setGuestCount] = useState(guests || 2);

    const [formData, setFormData] = useState({
        firstName: '',
        lastName: '',
        email: '',
        phone: '',
        specialRequests: ''
    });

    const handleInputChange = (e) => {
        setFormData({ ...formData, [e.target.name]: e.target.value });
    };

    const [couponCode, setCouponCode] = useState('');
    const [appliedCoupon, setAppliedCoupon] = useState(null);
    const [couponMessage, setCouponMessage] = useState({ type: '', text: '' });
    const [isApplyingCoupon, setIsApplyingCoupon] = useState(false);
    const [discountAmount, setDiscountAmount] = useState(0);

    const [paymentMethod, setPaymentMethod] = useState('online');
    const [onlineDiscountRules, setOnlineDiscountRules] = useState({ percentage: 0, max_amount: 0, status: 'active' });
    const [paymentGatewaySettings, setPaymentGatewaySettings] = useState({
        gateway: 'razorpay',
        key_id: '',
        mode: 'test',
        status: 'active'
    });

    useEffect(() => {
        const fetchSettings = async () => {
            try {
                const apiBase = process.env.REACT_APP_API_BASE_URL || 'http://127.0.0.1:8000/api/v1';
                const cleanApiBase = apiBase.replace(/\/+$/, '');

                // Fetch dynamic online discount settings from database
                const discountRes = await fetch(`${cleanApiBase}/settings/online-discount`);
                const discountData = await discountRes.json();
                if (discountData.success && discountData.data) {
                    setOnlineDiscountRules(discountData.data);
                }

                // Fetch dynamic payment gateway settings from database
                const gatewayRes = await fetch(`${cleanApiBase}/settings/payment-gateway`);
                const gatewayData = await gatewayRes.json();
                if (gatewayData.success && gatewayData.data) {
                    setPaymentGatewaySettings(gatewayData.data);
                    // If online payment gateway is inactive, automatically fall back to pay at hotel
                    if (gatewayData.data.status === 'inactive') {
                        setPaymentMethod('pay_at_hotel');
                    }
                }
            } catch (err) {
                console.error("Error fetching settings:", err);
            }
        };
        fetchSettings();
    }, []);

    const handleApplyCoupon = async () => {
        if (!couponCode.trim()) return;

        setIsApplyingCoupon(true);
        setCouponMessage({ type: '', text: '' });

        try {
            const apiBase = process.env.REACT_APP_API_BASE_URL || 'http://127.0.0.1:8000/api/v1';
            const cleanApiBase = apiBase.replace(/\/+$/, '');

            const response = await fetch(`${cleanApiBase}/coupons/validate`, {
                method: 'POST',
                headers: {
                    'Content-Type': 'application/json',
                    'Accept': 'application/json'
                },
                body: JSON.stringify({
                    code: couponCode.trim(),
                    total_amount: pricing.finalAmount
                })
            });

            const data = await response.json();

            if (data.status) {
                setAppliedCoupon(data.data.coupon);
                setDiscountAmount(data.data.discount_amount);
                setCouponMessage({ type: 'success', text: data.message });
            } else {
                setAppliedCoupon(null);
                setDiscountAmount(0);
                setCouponMessage({ type: 'error', text: data.message });
            }
        } catch (err) {
            console.error("Coupon validation error:", err);
            setAppliedCoupon(null);
            setDiscountAmount(0);
            setCouponMessage({ type: 'error', text: 'Error validating coupon. Please try again.' });
        } finally {
            setIsApplyingCoupon(false);
        }
    };

    const removeCoupon = () => {
        setAppliedCoupon(null);
        setCouponCode('');
        setDiscountAmount(0);
        setCouponMessage({ type: '', text: '' });
    };

    const baseFinalAmount = Math.max(0, pricing.finalAmount - discountAmount);

    const isDiscountActive = (onlineDiscountRules.status ?? 'active') !== 'inactive' && Number(onlineDiscountRules.percentage) > 0;
    let onlineDiscountAmount = 0;
    if (paymentMethod === 'online' && isDiscountActive) {
        onlineDiscountAmount = (baseFinalAmount * Number(onlineDiscountRules.percentage)) / 100;
        if (Number(onlineDiscountRules.max_amount) > 0 && onlineDiscountAmount > Number(onlineDiscountRules.max_amount)) {
            onlineDiscountAmount = Number(onlineDiscountRules.max_amount);
        }
    }

    const finalAmountAfterDiscount = Math.max(0, baseFinalAmount - onlineDiscountAmount);
    const handleConfirmBooking = async (e) => {
        e.preventDefault();
        setLoading(true);
        setError('');

        try {
            const apiBase = process.env.REACT_APP_API_BASE_URL || 'http://127.0.0.1:8000/api/v1';
            const token = localStorage.getItem('auth_token');

            if (!token) {
                setShowLoginModal(true);
                setLoading(false);
                return;
            }

            const cleanApiBase = apiBase.replace(/\/+$/, '');
            const isRateHawk = String(id).startsWith('rh_') || hotel?.provider === 'ratehawk';
            const endpoint = isRateHawk ? `${cleanApiBase}/bookings/create-hotel-booking` : `${cleanApiBase}/bookings`;

            const payload = isRateHawk ? {
                hotel_id: id || hotel.id || 'rh_10004834',
                hotel_name: hotel.name || 'RateHawk Hotel',
                book_hash: room?.book_hash || `p-rh-${id}-${Date.now()}`,
                room_type_name: room?.type || 'Standard Room',
                city: hotel.city || 'Global Destination',
                state: hotel.state || '',
                address: hotel.address || hotel.location || '',
                star_rating: hotel.star_rating || hotel.rating || 4,
                check_in_date: format(new Date(dates.startDate), 'yyyy-MM-dd'),
                check_out_date: format(new Date(dates.endDate), 'yyyy-MM-dd'),
                guest_firstName: formData.firstName,
                guest_lastName: formData.lastName,
                guest_email: formData.email || `${formData.firstName.toLowerCase()}@zoyotrip.com`,
                guest_phone: formData.phone,
                no_of_guests: guestCount,
                total_price: finalAmountAfterDiscount,
                payment_method: paymentMethod,
                special_requests: formData.specialRequests || ''
            } : {
                property_id: id || "1", // using id from params
                room_type_id: (!isNaN(room?.id) && room?.id !== null) ? Number(room.id) : null,
                check_in_date: format(new Date(dates.startDate), 'yyyy-MM-dd'),
                check_out_date: format(new Date(dates.endDate), 'yyyy-MM-dd'),
                guest_firstName: formData.firstName,
                guest_lastName: formData.lastName,
                guest_email: formData.email,
                guest_phone: formData.phone,
                no_of_guests: guestCount,
                total_price: finalAmountAfterDiscount,
                coupon_code: appliedCoupon ? appliedCoupon.code : null,
                room_details: room.type,
                special_requests: formData.specialRequests,
                payment_method: paymentMethod
            };

            const response = await fetch(endpoint, {
                method: 'POST',
                headers: {
                    'Content-Type': 'application/json',
                    'Accept': 'application/json',
                    'Authorization': `Bearer ${token}`
                },
                body: JSON.stringify(payload)
            });

            if (response.status === 401) {
                // Token is likely invalid or expired
                localStorage.removeItem('auth_token');
                setShowLoginModal(true);
                setLoading(false);
                return;
            }

            const data = await response.json();

            if (data.success) {
                if (data.data?.is_pay_at_hotel) {
                    navigate(`/payment-status?bookingId=${data.data.booking_id}&payAtHotel=true`, { replace: true });
                    return;
                }

                // Razorpay Order created: open payment modal
                if (data.data?.order_id) {
                    try {
                        await openRazorpayModal({
                            orderId: data.data.order_id,
                            amount: data.data.amount,
                            currency: data.data.currency || 'INR',
                            keyId: data.data.razorpay_key_id || paymentGatewaySettings.key_id,
                            name: 'ZoyoTrip',
                            description: `${room.type} Booking - ${hotel.name}`,
                            prefill: {
                                name: `${formData.firstName} ${formData.lastName}`.trim(),
                                email: formData.email,
                                phone: formData.phone
                            },
                            notes: {
                                booking_id: data.data.booking_id
                            },
                            onSuccess: async (razorpayResponse) => {
                                setLoading(true);
                                try {
                                    // STEP 3: Send verification payload to backend
                                    const verifyResponse = await fetch(`${cleanApiBase}/bookings/verify-payment`, {
                                        method: 'POST',
                                        headers: {
                                            'Content-Type': 'application/json',
                                            'Accept': 'application/json',
                                            'Authorization': `Bearer ${token}`
                                        },
                                        body: JSON.stringify({
                                            razorpay_order_id: razorpayResponse.razorpay_order_id,
                                            razorpay_payment_id: razorpayResponse.razorpay_payment_id,
                                            razorpay_signature: razorpayResponse.razorpay_signature,
                                            booking_id: data.data.booking_id
                                        })
                                    });

                                    const verifyData = await verifyResponse.json();

                                    if (verifyResponse.ok && verifyData.success) {
                                        navigate(`/payment-status?txnId=${razorpayResponse.razorpay_payment_id}&bookingId=${data.data.booking_id}&status=success`, { replace: true });
                                    } else {
                                        setError(verifyData.message || 'Payment signature verification failed. Please contact support.');
                                        setLoading(false);
                                    }
                                } catch (vErr) {
                                    console.error('Payment verification error:', vErr);
                                    setError('Payment received but verification check timed out. Please check your booking status.');
                                    setLoading(false);
                                }
                            },
                            onDismiss: () => {
                                setLoading(false);
                                setError('Payment modal was closed. You can retry payment or select Pay at Hotel.');
                            },
                            onFailure: (failedError) => {
                                setLoading(false);
                                setError(`Payment failed: ${failedError.description || failedError.reason || 'Transaction could not be completed.'}`);
                            }
                        });
                    } catch (rzpErr) {
                        console.error('Failed to open Razorpay modal:', rzpErr);
                        setError(rzpErr.message || 'Failed to open payment gateway.');
                        setLoading(false);
                    }
                } else if (data.data?.payment_url) {
                    // Fallback redirect if provided
                    window.location.replace(data.data.payment_url);
                } else {
                    const generatedBookingId = data.data.booking_id || `B-${Math.floor(100000 + Math.random() * 900000)}`;
                    navigate(`/payment-status?bookingId=${generatedBookingId}`, { replace: true });
                }
            } else {
                setError(data.message || 'Failed to process booking.');
                setLoading(false);
            }
        } catch (err) {
            console.error("Booking error:", err);
            setError('An error occurred while processing your booking. Please try again.');
        } finally {
            setLoading(false);
        }
    };


    return (
        <div className="bg-[#F8FAFC] min-h-screen py-8 pb-32 lg:pb-12">
            <DefaultSEO title="Checkout | ZoyoTrip" description="Complete your booking securely." />
            <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">

                {/* Page Title */}
                <div className="mb-8 mt-4 lg:mt-0">
                    <h1 className="text-3xl font-black text-gray-900 tracking-tight">Complete your booking</h1>
                    <p className="text-gray-500 font-medium mt-1">Almost there! Just a few more details.</p>
                </div>

                <div className="flex flex-col lg:flex-row gap-8">
                    {/* Left Column: Form & Security */}
                    <div className="lg:w-2/3 flex flex-col gap-6">

                        {error && (
                            <div className="bg-red-50 border-l-4 border-red-500 p-4 rounded-r-xl shadow-sm">
                                <p className="text-red-700 font-bold">{error}</p>
                            </div>
                        )}

                        {/* Guest Details Form */}
                        <div className="bg-white rounded-3xl shadow-sm border border-gray-100 p-4 sm:p-6 hover:shadow-md transition-shadow">
                            <h2 className="text-xl font-bold text-gray-900 mb-4">Guest Details</h2>

                            <form onSubmit={handleConfirmBooking} id="booking-form" className="space-y-4">
                                <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                                    <div>
                                        <label className="block text-xs font-bold text-gray-700 mb-1.5 uppercase tracking-wide">First Name <span className="text-red-500">*</span></label>
                                        <input type="text" name="firstName" value={formData.firstName} onChange={handleInputChange} required
                                            className="w-full bg-gray-50 border border-gray-200 rounded-xl px-4 py-2.5 text-gray-900 font-medium placeholder-gray-400 focus:outline-none focus:ring-2 focus:ring-blue-500 focus:bg-white transition-all shadow-sm" placeholder="e.g. John" />
                                    </div>
                                    <div>
                                        <label className="block text-xs font-bold text-gray-700 mb-1.5 uppercase tracking-wide">Last Name <span className="text-red-500">*</span></label>
                                        <input type="text" name="lastName" value={formData.lastName} onChange={handleInputChange} required
                                            className="w-full bg-gray-50 border border-gray-200 rounded-xl px-4 py-2.5 text-gray-900 font-medium placeholder-gray-400 focus:outline-none focus:ring-2 focus:ring-blue-500 focus:bg-white transition-all shadow-sm" placeholder="e.g. Doe" />
                                    </div>
                                </div>

                                <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                                    <div>
                                        <label className="block text-xs font-bold text-gray-700 mb-1.5 uppercase tracking-wide">Email Address <span className="text-gray-400 font-normal normal-case">(Optional)</span></label>
                                        <input type="email" name="email" value={formData.email} onChange={handleInputChange}
                                            className="w-full bg-gray-50 border border-gray-200 rounded-xl px-4 py-2.5 text-gray-900 font-medium placeholder-gray-400 focus:outline-none focus:ring-2 focus:ring-blue-500 focus:bg-white transition-all shadow-sm" placeholder="john@example.com" />
                                    </div>
                                    <div>
                                        <label className="block text-xs font-bold text-gray-700 mb-1.5 uppercase tracking-wide">Phone Number <span className="text-red-500">*</span></label>
                                        <input type="tel" name="phone" value={formData.phone} onChange={handleInputChange} required
                                            className="w-full bg-gray-50 border border-gray-200 rounded-xl px-4 py-2.5 text-gray-900 font-medium placeholder-gray-400 focus:outline-none focus:ring-2 focus:ring-blue-500 focus:bg-white transition-all shadow-sm" placeholder="+91 9876543210" />
                                    </div>
                                </div>

                                <div className="pt-1">
                                    <label className="block text-xs font-bold text-gray-700 mb-1.5 uppercase tracking-wide">Number of Guests <span className="text-red-500">*</span></label>
                                    <div className="flex items-center gap-4 bg-gray-50 border border-gray-200 rounded-xl px-2 py-1.5 w-fit shadow-sm">
                                        <button type="button"
                                            onClick={() => setGuestCount(Math.max(1, guestCount - 1))}
                                            disabled={guestCount <= 1}
                                            className={`w-8 h-8 rounded-lg flex items-center justify-center transition-all border ${guestCount <= 1 ? 'bg-gray-50 text-gray-300 cursor-not-allowed border-transparent' : 'text-gray-600 hover:bg-white hover:text-blue-600 hover:shadow-sm border-transparent hover:border-gray-200 bg-gray-100'}`}>
                                            <Minus size={16} />
                                        </button>
                                        <span className="font-bold text-lg w-6 text-center text-gray-900">{guestCount}</span>
                                        <button type="button"
                                            onClick={() => setGuestCount(Math.min(room?.capacity || 10, guestCount + 1))}
                                            disabled={guestCount >= (room?.capacity || 10)}
                                            className={`w-8 h-8 rounded-lg flex items-center justify-center transition-all border ${guestCount >= (room?.capacity || 10) ? 'bg-gray-50 text-gray-300 cursor-not-allowed border-transparent' : 'text-gray-600 hover:bg-white hover:text-blue-600 hover:shadow-sm border-transparent hover:border-gray-200 bg-gray-100'}`}>
                                            <Plus size={16} />
                                        </button>
                                    </div>
                                </div>

                                <div className="pt-1">
                                    <label className="block text-xs font-bold text-gray-700 mb-1.5 uppercase tracking-wide">Special Requests (Optional)</label>
                                    <textarea name="specialRequests" rows="2" value={formData.specialRequests} onChange={handleInputChange}
                                        className="w-full bg-gray-50 border border-gray-200 rounded-xl px-4 py-2.5 text-gray-900 font-medium placeholder-gray-400 focus:outline-none focus:ring-2 focus:ring-blue-500 focus:bg-white transition-all resize-none shadow-sm" placeholder="Any special requests? (e.g., late check-in)"></textarea>
                                </div>
                            </form>
                        </div>

                        {/* Payment Method Selection */}
                        <div className="bg-white rounded-3xl shadow-sm border border-gray-100 p-4 sm:p-6 hover:shadow-md transition-shadow">
                            <h2 className="text-xl font-bold text-gray-900 mb-4">Payment Method</h2>
                            <div className="space-y-3">
                                <label className={`flex items-center p-4 border rounded-xl transition-all ${
                                    paymentGatewaySettings.status === 'inactive'
                                        ? 'opacity-60 cursor-not-allowed bg-gray-50 border-gray-200'
                                        : paymentMethod === 'online'
                                            ? 'border-blue-500 bg-blue-50/50 cursor-pointer'
                                            : 'border-gray-200 hover:border-blue-200 hover:bg-gray-50 cursor-pointer'
                                }`}>
                                    <input
                                        type="radio"
                                        name="paymentMethod"
                                        value="online"
                                        disabled={paymentGatewaySettings.status === 'inactive'}
                                        checked={paymentMethod === 'online'}
                                        onChange={() => setPaymentMethod('online')}
                                        className="w-4 h-4 text-blue-600 border-gray-300 focus:ring-blue-500"
                                    />
                                    <div className="ml-3 flex-1">
                                        <div className="flex items-center gap-2">
                                            <span className="block text-sm font-bold text-gray-900">Pay Online</span>
                                            {paymentGatewaySettings.status === 'inactive' ? (
                                                <span className="bg-gray-200 text-gray-600 text-[10px] font-bold px-2 py-0.5 rounded-full uppercase tracking-wider">
                                                    Unavailable
                                                </span>
                                            ) : paymentGatewaySettings.mode === 'test' ? (
                                                <span className="bg-amber-100 text-amber-800 text-[10px] font-bold px-2 py-0.5 rounded-full uppercase tracking-wider">
                                                    Razorpay
                                                </span>
                                            ) : null}
                                        </div>
                                        <span className="block text-xs text-gray-500 mt-0.5">
                                            {paymentGatewaySettings.status === 'inactive'
                                                ? 'Online payments are temporarily disabled. Please choose Pay at Hotel.'
                                                : 'Pay securely with UPI, Credit/Debit Card, Net Banking or Wallet via Razorpay.'}
                                        </span>
                                        {paymentGatewaySettings.status !== 'inactive' && isDiscountActive && (
                                            <span className="inline-block mt-1 bg-green-100 text-green-700 text-[10px] font-bold px-2 py-0.5 rounded-full uppercase tracking-wider">
                                                Save {onlineDiscountRules.percentage}% up to ₹{onlineDiscountRules.max_amount}
                                            </span>
                                        )}
                                    </div>
                                </label>
                                <label className={`flex items-center p-4 border rounded-xl cursor-pointer transition-all ${paymentMethod === 'pay_at_hotel' ? 'border-blue-500 bg-blue-50/50' : 'border-gray-200 hover:border-blue-200 hover:bg-gray-50'}`}>
                                    <input
                                        type="radio"
                                        name="paymentMethod"
                                        value="pay_at_hotel"
                                        checked={paymentMethod === 'pay_at_hotel'}
                                        onChange={() => setPaymentMethod('pay_at_hotel')}
                                        className="w-4 h-4 text-blue-600 border-gray-300 focus:ring-blue-500"
                                    />
                                    <div className="ml-3 flex-1">
                                        <span className="block text-sm font-bold text-gray-900">Pay at Hotel</span>
                                        <span className="block text-xs text-gray-500 mt-0.5">Pay directly at the property when you arrive.</span>
                                    </div>
                                </label>
                            </div>
                        </div>

                        {/* Security Callout */}
                        <div className="bg-blue-50 border border-blue-100 rounded-2xl p-3 sm:p-4 flex flex-row items-start gap-3 shadow-sm hover:shadow-md transition-shadow">
                            <div className="bg-white p-2 rounded-xl shadow-sm border border-blue-100 flex-shrink-0">
                                <ShieldCheck size={20} className="text-blue-600" />
                            </div>
                            <div className="flex-1 text-left">
                                <h5 className="font-bold text-gray-900 text-sm sm:text-base mb-0.5">Highly Secure Booking</h5>
                                <p className="text-gray-600 text-[11px] sm:text-[13px] font-medium leading-snug">Your payment information is protected by industry-standard encryption. We never share your personal data with third parties.</p>
                            </div>
                        </div>
                    </div>

                    {/* Right Column: Order Summary (Receipt Style) */}
                    <div className="lg:w-1/3">
                        <div className="bg-white rounded-3xl shadow-xl border border-gray-100 overflow-hidden sticky top-24">
                            {/* Top Image Section */}
                            <div className="h-44 relative">
                                <img
                                    src={hotel.image || hotel.images?.[0] || "https://images.unsplash.com/photo-1566073771259-6a8506099945?ixlib=rb-4.0.3&auto=format&fit=crop&w=400&q=80"}
                                    alt={hotel.name}
                                    className="w-full h-full object-cover"
                                />
                                <div className="absolute inset-0 bg-gradient-to-t from-black/90 via-black/30 to-transparent"></div>
                                <div className="absolute bottom-4 left-4 right-4 text-white">
                                    <h3 className="font-black text-xl sm:text-2xl leading-tight mb-1 capitalize">{hotel.name}</h3>
                                    <div className="flex items-start text-gray-200 text-xs font-medium">
                                        <MapPin size={14} className="mr-1.5 mt-0.5 flex-shrink-0 text-blue-400" />
                                        <span className="leading-snug">{hotel.location}</span>
                                    </div>
                                </div>
                            </div>

                            {/* Booking Info */}
                            <div className="p-4 sm:p-5 pb-0">
                                <div className="grid grid-cols-2 gap-3 mb-4">
                                    <div className="flex flex-col gap-1.5 bg-gray-50/80 p-3 rounded-2xl border border-gray-100">
                                        <div className="flex items-center gap-1.5 text-[10px] text-gray-500 font-bold uppercase tracking-widest">
                                            <CheckCircle2 size={14} className="text-blue-500" /> Room
                                        </div>
                                        <div className="font-bold text-[13px] text-gray-900 leading-tight">{room.type}</div>
                                    </div>
                                    <div className="flex flex-col gap-1.5 bg-gray-50/80 p-3 rounded-2xl border border-gray-100">
                                        <div className="flex items-center gap-1.5 text-[10px] text-gray-500 font-bold uppercase tracking-widest">
                                            <Users size={14} className="text-blue-500" /> Guests
                                        </div>
                                        <div className="font-bold text-[13px] text-gray-900 leading-tight">{guestCount} Guest{guestCount > 1 ? 's' : ''}</div>
                                    </div>
                                </div>
                            </div>

                            {/* Divider Ticket Style */}
                            <div className="relative h-px flex items-center justify-center my-3">
                                <div className="w-full border-t-2 border-dashed border-gray-200 absolute"></div>
                                <div className="absolute -left-4 w-8 h-8 bg-[#F8FAFC] rounded-full shadow-inner"></div>
                                <div className="absolute -right-4 w-8 h-8 bg-[#F8FAFC] rounded-full shadow-inner"></div>
                            </div>

                            {/* Coupon Section */}
                            <div className="p-4 sm:p-5 pt-2">
                                <label className="block text-xs font-bold text-gray-700 mb-2 uppercase tracking-wide">Have a coupon code?</label>
                                {!appliedCoupon ? (
                                    <div className="flex gap-2">
                                        <input
                                            type="text"
                                            value={couponCode}
                                            onChange={(e) => setCouponCode(e.target.value.toUpperCase())}
                                            placeholder="Enter code (e.g. ZOYO10)"
                                            className="flex-1 bg-gray-50 border border-gray-200 rounded-xl px-3 py-2 text-sm font-bold text-gray-900 placeholder-gray-400 focus:outline-none focus:ring-2 focus:ring-blue-500 transition-all shadow-sm"
                                        />
                                        <button
                                            type="button"
                                            onClick={handleApplyCoupon}
                                            disabled={isApplyingCoupon}
                                            className="bg-gray-900 text-white font-bold px-4 py-2 rounded-xl hover:bg-gray-800 transition-colors text-sm shadow-sm disabled:opacity-70 flex items-center justify-center min-w-[70px]"
                                        >
                                            {isApplyingCoupon ? (
                                                <div className="w-4 h-4 border-2 border-white border-t-transparent rounded-full animate-spin"></div>
                                            ) : 'Apply'}
                                        </button>
                                    </div>
                                ) : (
                                    <div className="flex justify-between items-center bg-green-50 border border-green-200 rounded-xl px-3 py-2">
                                        <div className="flex flex-col">
                                            <span className="text-green-700 font-bold text-sm flex items-center gap-1">
                                                <CheckCircle2 size={14} /> {appliedCoupon.code} applied
                                            </span>
                                            <span className="text-green-600 text-xs font-medium">Saved ₹{discountAmount.toLocaleString('en-IN')}</span>
                                        </div>
                                        <button
                                            type="button"
                                            onClick={removeCoupon}
                                            className="text-red-500 text-xs font-bold hover:underline"
                                        >
                                            Remove
                                        </button>
                                    </div>
                                )}
                                {couponMessage.text && !appliedCoupon && (
                                    <p className={`mt-2 text-xs font-bold ${couponMessage.type === 'error' ? 'text-red-500' : 'text-green-600'}`}>
                                        {couponMessage.text}
                                    </p>
                                )}
                            </div>

                            {/* Price Breakdown */}
                            <div className="p-4 sm:p-5 pt-0">
                                <h4 className="font-bold text-gray-900 mb-4 text-base">Price Summary</h4>
                                <div className="space-y-3 text-sm mb-5">
                                    <div className="flex justify-between text-gray-600 font-medium">
                                        <span>₹{Math.round(pricing.totalPrice / pricing.totalNights).toLocaleString('en-IN')} x {pricing.totalNights} nights</span>
                                        <span className="text-gray-900 font-bold">₹{pricing.totalPrice.toLocaleString('en-IN')}</span>
                                    </div>
                                    <div className="flex justify-between text-gray-600 font-medium pb-3 border-b border-gray-100">
                                        <span>Taxes & Fees <span className="text-gray-400 text-[10px] ml-1 uppercase">(18%)</span></span>
                                        <span className="text-gray-900 font-bold">₹{pricing.taxes.toLocaleString('en-IN')}</span>
                                    </div>

                                    {appliedCoupon && (
                                        <div className="flex justify-between text-green-600 font-bold pb-3 border-b border-gray-100 animate-fade-in-down">
                                            <span>Coupon Discount ({appliedCoupon.code})</span>
                                            <span>-₹{discountAmount.toLocaleString('en-IN')}</span>
                                        </div>
                                    )}

                                    {onlineDiscountAmount > 0 && (
                                        <div className="flex justify-between text-blue-600 font-bold pb-3 border-b border-gray-100 animate-fade-in-down">
                                            <span>Online Payment Offer</span>
                                            <span>-₹{Math.round(onlineDiscountAmount).toLocaleString('en-IN')}</span>
                                        </div>
                                    )}

                                    <div className="flex justify-between items-end pt-1">
                                        <span className="font-bold text-gray-900 text-base">Total</span>
                                        <span className="font-black text-2xl text-gray-900 tracking-tight">₹{finalAmountAfterDiscount.toLocaleString('en-IN')}</span>
                                    </div>
                                </div>

                                <p className="text-[11px] text-gray-400 font-medium mb-6 text-center px-2 leading-relaxed">
                                    By proceeding, you agree to our <span className="text-blue-500 font-bold cursor-pointer hover:underline">Terms of Service</span> and <span className="text-blue-500 font-bold cursor-pointer hover:underline">Cancellation Policy</span>.
                                </p>

                                {error && (
                                    <div className="bg-red-50 border border-red-200 text-red-700 px-3 py-2.5 rounded-xl text-xs font-semibold mb-4 flex items-center gap-2">
                                        <span className="w-2 h-2 rounded-full bg-red-500 flex-shrink-0"></span>
                                        <span>{error}</span>
                                    </div>
                                )}

                                <button
                                    type="submit"
                                    form="booking-form"
                                    className="w-full bg-blue-600 text-white font-black py-4.5 rounded-2xl shadow-lg shadow-blue-600/30 hover:bg-blue-700 hover:shadow-blue-600/50 transition-all active:scale-95 text-lg tracking-wide disabled:opacity-70 disabled:cursor-not-allowed flex justify-center items-center h-14"
                                    disabled={loading}
                                >
                                    {loading ? (
                                        <div className="w-6 h-6 border-3 border-white border-t-transparent rounded-full animate-spin"></div>
                                    ) : (paymentMethod === 'pay_at_hotel' ? 'Confirm Booking' : 'Confirm & Pay')}
                                </button>
                            </div>
                        </div>
                    </div>
                </div>
            </div>

            <LoginModal
                isOpen={showLoginModal}
                onClose={() => {
                    setShowLoginModal(false);
                    // if they closed modal but aren't logged in, redirect them back to hotel details or home
                    if (!localStorage.getItem('auth_token')) {
                        navigate(id ? `/hotel/${id}` : '/');
                    }
                }}
                onSuccess={() => {
                    setShowLoginModal(false);
                }}
            />
        </div>
    );
};

export default BookingCheckoutPage;
